import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getBalanceScorecardData,
  updateBalanceScorecardFieldsets,
  getBalanceScorecardFieldsets,
  getBalanceScorecardOverrideReadOnly,
  setOverrideReadOnly,
  updatePrefilledData,
  fetchBalanceScorecardList,
} from "store/balanceScorecardSlice";
import { getLang, getTenantIsShadow } from "store/userSlice";
import useScrollPosition from "@react-hook/window-scroll";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import PreviewModal from "components/BalanceScorecard/PreviewModal";
import { toast } from "helpers/toast";
import { api } from "helpers/axios";
import { errorMessage, fieldDecimalCheck, formatNumberTo, navHeight, scrollInto, scrollToTop } from "helpers/common";
import * as Sentry from "@sentry/react";
import SettingRemarksModal from "components/ui/SettingRemarksModal";
import { remarksViewButtonString } from "helpers/settingRemarks";
import $ from "jquery";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { en, de } from 'yup-locales';
import { Controller, useFieldArray, useForm } from "react-hook-form";
import EditorInput from "components/ui/EditorInput";
import Link from "components/ui/Link";
import { renderHelpIcon } from "helpers/helpText";
import { Button, Field, Input, InputAffix, NativeSelect, Select } from "components/ui";

function InlineBoolean({ name, control, editableTranslate, readOnly = false }) {
  return <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <>
        <label className="d-inline-flex align-items-center mr-5">
          <input type="radio" checked={field.value === true} onChange={() => field.onChange(true)} disabled={readOnly} data-testid={`${name}-yes`} /> {editableTranslate('form.yes')}
        </label>
        <label className="d-inline-flex align-items-center">
          <input type="radio" checked={field.value === false} onChange={() => field.onChange(false)} disabled={readOnly} data-testid={`${name}-no`} /> {editableTranslate('form.no')}
        </label>
      </>
    )}
  />;
}

function DecimalPlaces({ name, control, editableTranslate, readOnly = false }) {
  return <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <>
        <label className="d-flex align-items-center">
          <input type="radio" checked={field.value === 0} onChange={() => field.onChange(0)} disabled={readOnly} data-testid={`${name}-0`} /> {editableTranslate('balance_scorecard.number_of_decimal_places_0')}
        </label>
        <label className="d-flex align-items-center">
          <input type="radio" checked={field.value === 1} onChange={() => field.onChange(1)} disabled={readOnly} data-testid={`${name}-1`} /> {editableTranslate('balance_scorecard.number_of_decimal_places_1')}
        </label>
        <label className="d-flex align-items-center">
          <input type="radio" checked={field.value === 2} onChange={() => field.onChange(2)} disabled={readOnly} data-testid={`${name}-2`} /> {editableTranslate('balance_scorecard.number_of_decimal_places_2')}
        </label>
        <label className="d-flex align-items-center">
          <input type="radio" checked={field.value === 3} onChange={() => field.onChange(3)} disabled={readOnly} data-testid={`${name}-3`} /> {editableTranslate('balance_scorecard.number_of_decimal_places_3')}
        </label>
      </>
    )}
  />;
}

function SystematicViewRow({ index, hasSub, data }) {
  const { editableTranslate } = useTranslation();

  const [opened, setOpened] = useState(index === 0 ? true : false);

  return <>
    <tr onClick={() => hasSub ? setOpened(!opened) : null} className={hasSub ? 'cursor-pointer' : ''}>
      {
        hasSub && <td className="w-1px v-middle">
          <i className={`open-row-icon bi bi-chevron-${opened ? 'up' : 'down'}`}></i>
        </td>
      }
      <td className="v-middle view-obj-td">
        <span className="view-obj-name">{data?.systematicObjectiveGridName} {!data?.systematicObjectiveAgreeObjective && <span className={`badge badge-pill badge-secondary`}>{editableTranslate('balance_scorecard.systematic_objective.optional')}</span>}</span>
        {
          data?.systematicObjectiveGridDescription && <span className="view-obj-desc">{data?.systematicObjectiveGridDescription}</span>
        }
      </td>
      <td className="v-middle">
        {
          (data?.systematicObjectiveAgreeObjective && !data?.systematicObjectiveGridWeighted) && <span>{editableTranslate('balanced_scorecard.systematic_objective.by_evaluator')}</span>
        }
        {
          data?.systematicObjectiveGridWeighted && <span>{data?.systematicObjectiveGridWeighted}%</span>
        }
      </td>
    </tr>
    {
      (hasSub && opened) && data?.systematicObjectivesubCriteria?.map((sub, i) => <tr key={i} className="view-sub-tr">
        <td></td>
        <td className="v-middle view-sub-obj-td">
          <div>
            <span className="view-sub-obj-name">{sub?.systematicSubObjectiveGridName} {!sub?.systematicSubObjectiveAgreeObjective && <span className={`badge badge-pill badge-secondary`}>{editableTranslate('balance_scorecard.systematic_objective.optional')}</span>}</span>
            {
              sub?.systematicSubObjectiveGridDescription && <span className="view-sub-obj-desc">{sub?.systematicSubObjectiveGridDescription}</span>
            }
          </div>
        </td>
        <td className="v-middle">
          {
            (sub?.systematicSubObjectiveAgreeObjective && !sub?.systematicSubObjectiveGridWeighted) && <span>{editableTranslate('balanced_scorecard.systematic_objective.by_evaluator')}</span>
          }
          {
            sub?.systematicSubObjectiveGridWeighted && <span>{sub?.systematicSubObjectiveGridWeighted}%</span>
          }
        </td>
      </tr>)
    }
  </>;
}

function SystematicView({ data, setValue, getValues, readOnly }) {
  const { editableTranslate } = useTranslation();

  const hasSub = useMemo(() => {
    return data?.shouldHasSubCriteria || false;
  }, [data]);

  const arr = useMemo(() => {
    if (hasSub) return data?.systematicObjectiveDatagrid_subCriteria || [];
    return data?.systematicObjectiveDatagrid || [];
  }, [hasSub, data]);

  return <div>
    <table data-testid="systematic-view-table" className="table systematic-view-table">
      <thead>
        <tr>
          {
            hasSub && <th className="w-1px"></th>
          }
          <th>{editableTranslate('balance_scorecard.systematic_objective.objective')}</th>
          <th>{editableTranslate('balance_scorecard.systematic_objective.weighted')}</th>
        </tr>
      </thead>
      <tbody>
        {
          arr.map((obj, i) => <SystematicViewRow key={i} index={i} hasSub={hasSub} data={obj} />)
        }
      </tbody>
    </table>
  </div>;
}

function SystematicObjectiveRow({ index, handleRemoveObjective, handleShareChange, shareOptions, allAuto, total, shareData, adjustShareData, control, register, errors, watch, readOnly = false }) {
  const { translate, editableTranslate } = useTranslation();

  const currentId = watch(`systematicObjectiveDatagrid.${index}.id`);

  const currentAgreed = watch(`systematicObjectiveDatagrid.${index}.systematicObjectiveAgreeObjective`);

  const currentWeightedMode = watch(`systematicObjectiveDatagrid.${index}.weighted.mode`);

  const currentWeightedValue = watch(`systematicObjectiveDatagrid.${index}.weighted.value`);

  const currentWeightedShareWith = watch(`systematicObjectiveDatagrid.${index}.weighted.share_with`);

  useEffect(() => {
    adjustShareData();
  }, [currentAgreed, currentWeightedMode]);

  return <tr>
    <td>{index + 1}</td>
    <td>
      <input type="hidden" {...register(`systematicObjectiveDatagrid.${index}.id`)} />
      <Input type="text" error={!!errors?.systematicObjectiveDatagrid?.[index]?.systematicObjectiveGridName} {...register(`systematicObjectiveDatagrid.${index}.systematicObjectiveGridName`)} readOnly={readOnly} />
    </td>
    <td>
      <Input type="text" {...register(`systematicObjectiveDatagrid.${index}.systematicObjectiveGridDescription`)} readOnly={readOnly} />
    </td>
    <td>
      <input type="checkbox" className="mx-auto" {...register(`systematicObjectiveDatagrid.${index}.systematicObjectiveAgreeObjective`)} readOnly={readOnly} data-testid={`systematic-objective-${index}-agree-objective`} />
    </td>
    <td>
      {
        currentAgreed && <div className="d-flex">
          <NativeSelect className={currentWeightedMode === 'manual' ? 'has-suffix' : ''} {...register(`systematicObjectiveDatagrid.${index}.weighted.mode`)} disabled={readOnly}>
            <option value="by_evaluator">{translate('balanced_scorecard.systematic_objective.by_evaluator')}</option>
            <option value="auto">{translate('balanced_scorecard.systematic_objective.auto')}</option>
            <option value="manual">{translate('balanced_scorecard.systematic_objective.manual')}</option>
          </NativeSelect>
          {
            currentWeightedMode === 'manual' && <InputAffix suffix="%" error={!!(!currentWeightedValue || errors?.systematicObjectiveDatagrid?.[index]?.weighted?.value)} readOnly={readOnly} className="max-w-80">
              <Input type="text" {...register(`systematicObjectiveDatagrid.${index}.weighted.value`)} readOnly={readOnly} />
            </InputAffix>
          }
        </div>
      }
    </td>
    <td>
      {
        allAuto ? <span>{editableTranslate('balance_scorecard.systematic_objective.implication.all_auto')}</span>
          : <>
            {
              !currentAgreed ? <span>{editableTranslate('balance_scorecard.systematic_objective.implication.optional')}</span>
                : <>
                  {
                    (currentWeightedMode === 'manual' && currentWeightedShareWith.length === 0) && <span>{editableTranslate('balance_scorecard.systematic_objective.implication.manual')} {currentWeightedValue || '...'}%.</span>
                  }
                  {
                    (currentWeightedMode === 'manual' && currentWeightedValue && currentWeightedShareWith.length > 0) && <span> {editableTranslate('balance_scorecard.systematic_objective.implication.shared_with')} {shareData?.[currentId]?.share_with_text}.</span>
                  }
                  {
                    (currentWeightedMode === 'by_evaluator' && !currentWeightedValue) && <span>{editableTranslate('balance_scorecard.systematic_objective.implication.by_evaluator')}</span>
                  }
                  {
                    (currentWeightedMode === 'auto' && !currentWeightedValue && shareOptions.length > 0) &&
                    <span>
                      {editableTranslate('balance_scorecard.systematic_objective.implication.shares_with')} <NativeSelect data-testid="shares-with-select" className="d-inline-block w-auto" error={!shareData?.[currentId]?.shared_from} value={shareData?.[currentId]?.shared_from || ''} onChange={e => handleShareChange(currentId, e.target.value)}>
                        <option value={''}></option>
                        {shareOptions.map((opt, i) => <option key={i} value={opt.value}>{opt.label}</option>)}
                      </NativeSelect>.
                    </span>
                  }
                </>
            }
          </>
      }
    </td>
    {
      (total > 1 && !readOnly) && <td>
        <Button variant="ghost" size="inline" tone="danger" className="text-bigger" icon="bi-trash" onClick={() => handleRemoveObjective(index)} data-testid={`systematic-objective-${index}-remove`} />
      </td>
    }
  </tr>;
}

function Systematic({ fieldsArray, control, register, setValue, getValues, watch, watchAll, errors, readOnly = false, helpsObj }) {
  const { fields, append, remove } = fieldsArray;

  const { translate, editableTranslate } = useTranslation();

  const [shareOptions, countAuto, shareData] = useMemo(() => {
    const currentSystematic = watchAll?.systematicObjectiveDatagrid;

    const opts = [];
    let count = 0;
    const shareObj = {};

    for (let i = 0; i < currentSystematic.length; i++) {
      const obj = currentSystematic[i];

      if (obj?.weighted?.mode === 'auto') count++;

      if (obj?.weighted?.mode === 'manual' && obj?.weighted?.value) {
        opts.push({ value: obj?.id, label: (i + 1) });
      }

      let shared_from = null;
      let share_with_text = null;
      if (obj?.weighted?.mode === 'auto') {
        for (const obj2 of currentSystematic) {
          if (obj2?.weighted?.mode === 'manual' && obj2?.weighted?.value && obj2?.weighted?.share_with?.includes(obj?.id)) {
            shared_from = obj2?.id;
          }
        }
      } else if (obj?.weighted?.value) {
        const labels = [];
        share_with_text = '';
        for (const [j, obj2] of currentSystematic.entries()) {
          if (obj?.weighted?.share_with?.includes(obj2?.id)) {
            labels.push((j + 1));
          }
        }

        if (labels.length > 1) {
          share_with_text = labels.slice(0, -1).join(', ') + ' ' + translate('balance_scorecard.systematic_objective.implication.and', 'and') + ' ' + labels.slice(-1);
        } else {
          share_with_text = labels[0] || '';
        }
      }

      shareObj[obj?.id] = { shared_from, share_with_text };
    }

    return [opts, count, shareObj];
  }, [watchAll]);

  const handleAddObjectiveRow = () => {
    append({
      id: crypto.randomUUID(),
      systematicObjectiveGridName: null,
      systematicObjectiveGridDescription: null,
      systematicObjectiveAgreeObjective: false,
      systematicObjectiveDefineWeightingObjective: false,
      systematicObjectiveGridWeighted: null,
      weighted: {
        mode: 'by_evaluator',
        value: null,
        share_with: []
      }
    });
  };

  const adjustShareData = () => {
    const current = getValues('systematicObjectiveDatagrid');

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];

      if (!obj?.systematicObjectiveAgreeObjective) {
        setValue(`systematicObjectiveDatagrid.${i}.weighted.mode`, 'by_evaluator');
        setValue(`systematicObjectiveDatagrid.${i}.weighted.value`, null);
        setValue(`systematicObjectiveDatagrid.${i}.weighted.share_with`, []);
      } else {
        if (obj?.weighted?.mode !== 'manual') {
          setValue(`systematicObjectiveDatagrid.${i}.weighted.value`, null);
          setValue(`systematicObjectiveDatagrid.${i}.weighted.share_with`, []);
        } else if (obj?.weighted?.share_with?.length > 0) {
          const filtered = obj?.weighted?.share_with?.filter(sw => {
            for (const obj2 of current) {
              if (obj2?.id === sw) return true;
            }
            return false;
          });
          setValue(`systematicObjectiveDatagrid.${i}.weighted.share_with`, filtered);
        }
      }
    }
  };

  const handleRemoveObjective = (index) => {
    remove(index);
    adjustShareData();
  };

  const handleShareChange = (id, sourceId) => {
    const current = watchAll?.systematicObjectiveDatagrid;

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];

      if (obj.id === sourceId) {
        if (!obj?.weighted?.share_with?.includes(id)) {
          const newArr = [...obj?.weighted?.share_with, id];
          setValue(`systematicObjectiveDatagrid.${i}.weighted.share_with`, newArr);
        }
      } else {
        if (obj?.weighted?.share_with?.includes(id)) {
          const newArr = obj?.weighted?.share_with?.filter(sw => sw !== id);
          setValue(`systematicObjectiveDatagrid.${i}.weighted.share_with`, newArr);
        }
      }
    }
  };

  return <>
    <div className="table-container">
      <table className="table table-levels">
        <thead>
          <tr>
            <th>{editableTranslate('form.table_number', 'Nr.')}</th>
            <th>{editableTranslate('balance_scorecard.systematic_objective.name')}</th>
            <th>{editableTranslate('balance_scorecard.systematic_objective.description')}</th>
            <th className="text-center text-nowrap">
              {editableTranslate('balance_scorecard.systematic_objective.agree_objective')}
              {renderHelpIcon(helpsObj, 'systematicObjectiveAgreeObjective')}
            </th>
            <th className="text-nowrap">
              {editableTranslate('balance_scorecard.systematic_objective.weighted')}
              {renderHelpIcon(helpsObj, 'systematicObjectiveWeighted')}
            </th>
            <th className="text-nowrap">
              {editableTranslate('balance_scorecard.systematic_objective.implications')}
              {renderHelpIcon(helpsObj, 'systematicObjectiveImplications')}
            </th>
            {
              (fields.length > 1 && !readOnly) && <th></th>
            }
          </tr>
        </thead>
        <tbody>
          {
            fields.map((field, i) => <SystematicObjectiveRow key={field.id} index={i}
              handleRemoveObjective={handleRemoveObjective}
              handleShareChange={handleShareChange}
              shareOptions={shareOptions}
              allAuto={countAuto === fields.length}
              total={fields.length}
              shareData={shareData}
              adjustShareData={adjustShareData}
              control={control}
              register={register}
              errors={errors}
              watch={watch}
              readOnly={readOnly} />)
          }
        </tbody>
      </table>
    </div>
    {
      !readOnly && <div>
        <Button variant="primary" onClick={handleAddObjectiveRow} data-testid="add-another-button">
          <i className="bi bi-plus"></i> {editableTranslate('form.buttom.add_another')}
        </Button>
      </div>
    }
  </>;
}

function SystematicSubSecondaryObjectiveRow({ index, primaryKey, primaryIndex, handleRemoveObjective, handleShareChange, shareOptions, allAuto, total, shareData, adjustShareData, control, register, errors, watch, readOnly = false }) {
  const { translate, editableTranslate } = useTranslation();

  const currentId = watch(`${primaryKey}.${index}.id`);

  const currentAgreed = watch(`${primaryKey}.${index}.systematicSubObjectiveAgreeObjective`);

  const currentWeightedMode = watch(`${primaryKey}.${index}.weighted.mode`);

  const currentWeightedValue = watch(`${primaryKey}.${index}.weighted.value`);

  const currentWeightedShareWith = watch(`${primaryKey}.${index}.weighted.share_with`);

  useEffect(() => {
    adjustShareData();
  }, [currentAgreed, currentWeightedMode]);

  return <tr>
    <td className={`arrow-td ${index < total - 1 ? 'has-next' : ''} position-relative`}></td>
    <td>{index + 1}</td>
    <td>
      <input type="hidden" {...register(`${primaryKey}.${index}.id`)} />
      <Input type="text" error={!!errors?.systematicObjectiveDatagrid_subCriteria?.[primaryIndex]?.systematicObjectivesubCriteria?.[index]?.systematicSubObjectiveGridName} {...register(`${primaryKey}.${index}.systematicSubObjectiveGridName`)} readOnly={readOnly} />
    </td>
    <td>
      <Input type="text" {...register(`${primaryKey}.${index}.systematicSubObjectiveGridDescription`)} readOnly={readOnly} />
    </td>
    <td>
      <input type="checkbox" className="mx-auto" {...register(`${primaryKey}.${index}.systematicSubObjectiveAgreeObjective`)} readOnly={readOnly} data-testid={`systematic-sub-objective-${index}-agree-objective`} />
    </td>
    <td>
      {
        currentAgreed && <div className="d-flex">
          <NativeSelect className={currentWeightedMode === 'manual' ? 'has-suffix' : ''} {...register(`${primaryKey}.${index}.weighted.mode`)} disabled={readOnly}>
            <option value="by_evaluator">{translate('balanced_scorecard.systematic_objective.by_evaluator')}</option>
            <option value="auto">{translate('balanced_scorecard.systematic_objective.auto')}</option>
            <option value="manual">{translate('balanced_scorecard.systematic_objective.manual')}</option>
          </NativeSelect>
          {
            currentWeightedMode === 'manual' && <InputAffix suffix="%" error={!!(!currentWeightedValue || errors?.systematicObjectiveDatagrid_subCriteria?.[primaryIndex]?.systematicObjectivesubCriteria?.[index]?.weighted?.value)} readOnly={readOnly} className="max-w-80">
              <Input type="text" {...register(`${primaryKey}.${index}.weighted.value`)} readOnly={readOnly} />
            </InputAffix>
          }
        </div>
      }
    </td>
    <td>
      {
        allAuto ? <span>{editableTranslate('balance_scorecard.systematic_objective.implication.all_auto')}</span>
          : <>
            {
              !currentAgreed ? <span>{editableTranslate('balance_scorecard.systematic_objective.implication.optional')}</span>
                : <>
                  {
                    (currentWeightedMode === 'manual' && currentWeightedShareWith.length === 0) && <span>{editableTranslate('balance_scorecard.systematic_objective.implication.manual')} {currentWeightedValue}%.</span>
                  }
                  {
                    (currentWeightedMode === 'manual' && currentWeightedValue && currentWeightedShareWith.length > 0) && <span> {editableTranslate('balance_scorecard.systematic_objective.implication.shared_with')} {shareData?.[currentId]?.share_with_text}.</span>
                  }
                  {
                    (currentWeightedMode === 'by_evaluator' && !currentWeightedValue) && <span>{editableTranslate('balance_scorecard.systematic_objective.implication.by_evaluator')}</span>
                  }
                  {
                    (currentWeightedMode === 'auto' && !currentWeightedValue && shareOptions.length > 0) &&
                    <span>
                      {editableTranslate('balance_scorecard.systematic_objective.implication.shares_with')} <NativeSelect data-testid="shares-with-select" className="d-inline-block w-auto" error={!shareData?.[currentId]?.shared_from} value={shareData?.[currentId]?.shared_from || ''} onChange={e => handleShareChange(currentId, e.target.value)}>
                        <option value={''}></option>
                        {shareOptions.map((opt, i) => <option key={i} value={opt.value}>{opt.label}</option>)}
                      </NativeSelect>.
                    </span>
                  }
                </>
            }
          </>
      }
    </td>
    {
      !readOnly && <td>
        <Button variant="ghost" size="inline" tone="danger" className={`text-bigger ${total > 1 ? '' : 'd-none'}`} icon="bi-trash" onClick={() => handleRemoveObjective(index)} data-testid={`systematic-sub-objective-${index}-remove`} />
      </td>
    }
  </tr>;
}

function SystematicSubSecondary({ index, fieldsArray, control, register, setValue, getValues, watch, watchAll, errors, readOnly = false }) {
  const arrKey = `systematicObjectiveDatagrid_subCriteria.${index}.systematicObjectivesubCriteria`;

  const { fields, append, remove } = fieldsArray;

  const { translate, editableTranslate } = useTranslation();

  const [shareOptions, countAuto, shareData] = useMemo(() => {
    const currentSystematic = watchAll?.systematicObjectiveDatagrid_subCriteria?.[index]?.systematicObjectivesubCriteria;

    const opts = [];
    let count = 0;
    const shareObj = {};

    for (let i = 0; i < currentSystematic.length; i++) {
      const obj = currentSystematic[i];

      if (obj?.weighted?.mode === 'auto') count++;

      if (obj?.weighted?.mode === 'manual' && obj?.weighted?.value) {
        opts.push({ value: obj?.id, label: (i + 1) });
      }

      let shared_from = null;
      let share_with_text = null;
      if (obj?.weighted?.mode === 'auto') {
        for (const obj2 of currentSystematic) {
          if (obj2?.weighted?.mode === 'manual' && obj2?.weighted?.value && obj2?.weighted?.share_with?.includes(obj?.id)) {
            shared_from = obj2?.id;
          }
        }
      } else if (obj?.weighted?.value) {
        const labels = [];
        share_with_text = '';
        for (const [j, obj2] of currentSystematic.entries()) {
          if (obj?.weighted?.share_with?.includes(obj2?.id)) {
            labels.push((j + 1));
          }
        }

        if (labels.length > 1) {
          share_with_text = labels.slice(0, -1).join(', ') + ' ' + translate('balance_scorecard.systematic_objective.implication.and', 'and') + ' ' + labels.slice(-1);
        } else {
          share_with_text = labels[0] || '';
        }
      }

      shareObj[obj?.id] = { shared_from, share_with_text };
    }

    return [opts, count, shareObj];
  }, [watchAll]);

  const handleAddObjectiveRow = () => {
    append({
      id: crypto.randomUUID(),
      systematicSubObjectiveGridName: null,
      systematicSubObjectiveGridDescription: null,
      systematicSubObjectiveAgreeObjective: false,
      systematicSubObjectiveDefineWeightingObjective: false,
      systematicSubObjectiveGridWeighted: null,
      weighted: {
        mode: 'by_evaluator',
        value: null,
        share_with: []
      }
    });
  };

  const adjustShareData = () => {
    const current = getValues(arrKey);

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];

      if (!obj?.systematicSubObjectiveAgreeObjective) {
        setValue(`${arrKey}.${i}.weighted.mode`, 'by_evaluator');
        setValue(`${arrKey}.${i}.weighted.value`, null);
        setValue(`${arrKey}.${i}.weighted.share_with`, []);
      } else {
        if (obj?.weighted?.mode !== 'manual') {
          setValue(`${arrKey}.${i}.weighted.value`, null);
          setValue(`${arrKey}.${i}.weighted.share_with`, []);
        } else if (obj?.weighted?.share_with?.length > 0) {
          const filtered = obj?.weighted?.share_with?.filter(sw => {
            for (const obj2 of current) {
              if (obj2?.id === sw) return true;
            }
            return false;
          });
          setValue(`${arrKey}.${i}.weighted.share_with`, filtered);
        }
      }
    }
  };

  const handleRemoveObjective = (index) => {
    remove(index);
    adjustShareData();
  };

  const handleShareChange = (id, sourceId) => {
    const current = watchAll?.systematicObjectiveDatagrid_subCriteria?.[index]?.systematicObjectivesubCriteria;

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];

      if (obj.id === sourceId) {
        if (!obj?.weighted?.share_with?.includes(id)) {
          const newArr = [...obj?.weighted?.share_with, id];
          setValue(`${arrKey}.${i}.weighted.share_with`, newArr);
        }
      } else {
        if (obj?.weighted?.share_with?.includes(id)) {
          const newArr = obj?.weighted?.share_with?.filter(sw => sw !== id);
          setValue(`${arrKey}.${i}.weighted.share_with`, newArr);
        }
      }
    }
  };

  return <>
    {
      fields.map((field, i) => <SystematicSubSecondaryObjectiveRow key={field.id} index={i} primaryKey={arrKey} primaryIndex={index}
        handleRemoveObjective={handleRemoveObjective}
        handleShareChange={handleShareChange}
        shareOptions={shareOptions}
        allAuto={countAuto === fields.length}
        total={fields.length}
        shareData={shareData}
        adjustShareData={adjustShareData}
        control={control}
        register={register}
        errors={errors}
        watch={watch}
        readOnly={readOnly} />)
    }
    {
      !readOnly && <tr className="tr-no-border-top">
        <td colSpan={2}></td>
        <td colSpan={!readOnly ? 6 : 5} className="pt-0">
          <Button variant="secondary" onClick={handleAddObjectiveRow} data-testid="add-another-button">
            <i className="bi bi-plus"></i> {editableTranslate('form.buttom.add_another')}
          </Button>
        </td>
      </tr>
    }
  </>;
}

function SystematicSubPrimaryObjectiveRow({ index, handleRemoveObjective, total, adjustShareData, control, register, setValue, getValues, watch, watchAll, errors, readOnly = false }) {
  const { translate, editableTranslate } = useTranslation();

  const currentAgreed = watch(`systematicObjectiveDatagrid_subCriteria.${index}.systematicObjectiveAgreeObjective`);

  const currentWeightedMode = watch(`systematicObjectiveDatagrid_subCriteria.${index}.weighted.mode`);

  const currentWeightedValue = watch(`systematicObjectiveDatagrid_subCriteria.${index}.weighted.value`);

  const subCriteriaArray = useFieldArray({
    control,
    name: `systematicObjectiveDatagrid_subCriteria.${index}.systematicObjectivesubCriteria`,
  });

  useEffect(() => {
    adjustShareData();
  }, [currentAgreed, currentWeightedMode]);

  return <>
    <tr className="primary-tr">
      <td>{index + 1}</td>
      <td></td>
      <td colSpan={2}>
        <input type="hidden" {...register(`systematicObjectiveDatagrid_subCriteria.${index}.id`)} />
        <Input type="text" error={!!errors?.systematicObjectiveDatagrid_subCriteria?.[index]?.systematicObjectiveGridName} className="w-70" {...register(`systematicObjectiveDatagrid_subCriteria.${index}.systematicObjectiveGridName`)} readOnly={readOnly} />
      </td>
      <td>
        <input type="checkbox" className="mx-auto" {...register(`systematicObjectiveDatagrid_subCriteria.${index}.systematicObjectiveAgreeObjective`)} readOnly={readOnly} data-testid={`systematic-objective-sub-criteria-${index}-agree-objective`} />
      </td>
      <td>
        {
          currentAgreed && <div className="d-flex">
            <NativeSelect className={currentWeightedMode === 'manual' ? 'has-suffix' : ''} {...register(`systematicObjectiveDatagrid_subCriteria.${index}.weighted.mode`)} disabled={readOnly}>
              <option value="by_evaluator">{translate('balanced_scorecard.systematic_objective.by_evaluator')}</option>
              <option value="manual">{translate('balanced_scorecard.systematic_objective.manual')}</option>
            </NativeSelect>
            {
              currentWeightedMode === 'manual' && <InputAffix suffix="%" error={!!(!currentWeightedValue || errors?.systematicObjectiveDatagrid_subCriteria?.[index]?.weighted?.value)} readOnly={readOnly} className="max-w-80">
                <Input type="text" {...register(`systematicObjectiveDatagrid_subCriteria.${index}.weighted.value`)} readOnly={readOnly} />
              </InputAffix>
            }
          </div>
        }
      </td>
      <td>
        {
          !currentAgreed ? <span>{editableTranslate('balance_scorecard.systematic_objective.implication.optional')}</span>
            : <>
              {
                (currentWeightedMode === 'manual') && <span>{editableTranslate('balance_scorecard.systematic_objective.implication.manual')} {currentWeightedValue}%.</span>
              }
              {
                (currentWeightedMode === 'by_evaluator' && !currentWeightedValue) && <span>{editableTranslate('balance_scorecard.systematic_objective.implication.by_evaluator')}</span>
              }
            </>
        }
      </td>
      {
        !readOnly && <td>
          <Button variant="ghost" size="inline" tone="danger" className={`text-bigger ${total > 1 ? '' : 'd-none'}`} icon="bi-trash" onClick={() => handleRemoveObjective(index)} data-testid={`systematic-objective-sub-criteria-${index}-remove`} />
        </td>
      }
    </tr>
    <SystematicSubSecondary
      index={index}
      fieldsArray={subCriteriaArray}
      control={control} register={register} setValue={setValue} getValues={getValues} watch={watch} watchAll={watchAll} errors={errors} readOnly={readOnly} />
  </>
}

function SystematicSubPrimary({ fieldsArray, control, register, setValue, getValues, watch, watchAll, errors, readOnly = false, helpsObj }) {
  const { fields, append, remove } = fieldsArray;

  const { editableTranslate } = useTranslation();

  const handleAddObjectiveRow = () => {
    append({
      id: crypto.randomUUID(),
      systematicObjectiveGridName: null,
      systematicObjectiveAgreeObjective: false,
      systematicObjectiveDefineWeightingObjective: false,
      systematicObjectiveGridWeighted: null,
      weighted: {
        mode: 'by_evaluator',
        value: null
      },
      systematicObjectivesubCriteria: [{
        id: crypto.randomUUID(),
        systematicSubObjectiveGridName: null,
        systematicSubObjectiveGridDescription: null,
        systematicSubObjectiveAgreeObjective: false,
        systematicSubObjectiveDefineWeightingObjective: false,
        systematicSubObjectiveGridWeighted: null,
        weighted: {
          mode: 'by_evaluator',
          value: null,
          share_with: []
        }
      }]
    });
  };

  const adjustShareData = () => {
    const current = getValues('systematicObjectiveDatagrid_subCriteria');

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];

      if (!obj?.systematicObjectiveAgreeObjective) {
        setValue(`systematicObjectiveDatagrid_subCriteria.${i}.weighted.mode`, 'by_evaluator');
        setValue(`systematicObjectiveDatagrid_subCriteria.${i}.weighted.value`, null);
      } else if (obj?.weighted?.mode !== 'manual') {
        setValue(`systematicObjectiveDatagrid_subCriteria.${i}.weighted.value`, null);
      }
    }
  };

  const handleRemoveObjective = (index) => {
    remove(index);
  };

  return <>
    <div className="table-container">
      <table className="table table-levels">
        <thead>
          <tr>
            <th colSpan={2} className="text-center">{editableTranslate('form.table_number', 'Nr.')}</th>
            <th>{editableTranslate('balance_scorecard.systematic_objective.name_description')}</th>
            <th></th>
            <th className="text-center text-nowrap">
              {editableTranslate('balance_scorecard.systematic_objective.agree_objective')}
              {renderHelpIcon(helpsObj, 'systematicObjectiveAgreeObjective')}
            </th>
            <th className="text-nowrap">
              {editableTranslate('balance_scorecard.systematic_objective.weighted')}
              {renderHelpIcon(helpsObj, 'systematicObjectiveWeighted')}
            </th>
            <th className="text-nowrap">
              {editableTranslate('balance_scorecard.systematic_objective.implications')}
              {renderHelpIcon(helpsObj, 'systematicObjectiveImplications')}
            </th>
            {
              !readOnly && <th></th>
            }
          </tr>
        </thead>
        <tbody>
          {
            fields.map((field, i) => <SystematicSubPrimaryObjectiveRow key={field.id} index={i}
              handleRemoveObjective={handleRemoveObjective}
              total={fields.length}
              adjustShareData={adjustShareData}
              control={control}
              register={register}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
              watchAll={watchAll}
              errors={errors}
              readOnly={readOnly} />)
          }
        </tbody>
      </table>
    </div>
    {
      !readOnly && <div>
        <Button variant="primary" onClick={handleAddObjectiveRow} data-testid="add-another-button">
          <i className="bi bi-plus"></i> {editableTranslate('form.buttom.add_another')}
        </Button>
      </div>
    }
  </>;
}

function PerformanceLevelGrid({ fieldsArray, name, showMandatory, control, register, errors, readOnly = false, helpsObj }) {
  const { fields, append, remove } = fieldsArray;

  const { editableTranslate } = useTranslation();

  const handleAddRow = () => {
    const obj = {};
    obj[name + 'Level'] = null;
    obj[name + 'Value'] = null;
    obj[name + 'Description'] = null;
    obj[name + 'Fulfillment'] = false;
    obj[name + 'EdgeValue'] = false;

    append(obj);
  };

  return <div className="table-responsive">
    <table className="table table-levels w-auto">
      <thead>
        <tr>
          <th className="text-nowrap">
            {editableTranslate('balance_scorecard.level')}
            {renderHelpIcon(helpsObj, 'performanceLevel-level')}
          </th>
          <th className="text-nowrap">
            {editableTranslate('balance_scorecard.value')}
            {renderHelpIcon(helpsObj, 'performanceLevel-value')}
          </th>
          <th>
            {editableTranslate('balance_scorecard.description')}
          </th>
          <th className="text-center text-nowrap">
            {editableTranslate('balance_scorecard.fulfillment')}
            {renderHelpIcon(helpsObj, 'performanceLevel-fulfillment')}
          </th>
          {
            showMandatory && <th className="text-center text-nowrap">
              {editableTranslate('balance_scorecard.edge_value')}
              {renderHelpIcon(helpsObj, 'performanceLevel-edgeValue')}
            </th>
          }
          {
            (fields.length > 1 && !readOnly) && <th></th>
          }
        </tr>
      </thead>
      <tbody>
        {
          fields.map((field, index) => (
            <tr key={field.id} data-testid={`row-${field?.id}`}>
              <td>
                <Input type="text" error={!!errors?.[name + 'PerformanceLevelGrid']?.[index]?.[name + 'Level']} className="input-w-80" {...register(`${name}PerformanceLevelGrid.${index}.${name}Level`)} readOnly={readOnly} />
              </td>
              <td>
                <Input type="text" error={!!errors?.[name + 'PerformanceLevelGrid']?.[index]?.[name + 'Value']} className="input-w-80" {...register(`${name}PerformanceLevelGrid.${index}.${name}Value`)} readOnly={readOnly} />
              </td>
              <td>
                <Input type="text" {...register(`${name}PerformanceLevelGrid.${index}.${name}Description`)} readOnly={readOnly} />
              </td>
              <td className="text-center">
                <Controller
                  name={`${name}PerformanceLevelGrid.${index}.${name}Fulfillment`}
                  control={control}
                  render={({ field }) => <input type="radio" checked={field.value === true} onClick={() => field.onChange(!field.value)} onChange={() => field.onChange(!field.value)} disabled={readOnly} data-testid={`${name}-performance-level-${index}-fulfillment`} />}
                />
              </td>
              {
                showMandatory && <td className="text-center">
                  <input type="checkbox" className="mx-auto" {...register(`${name}PerformanceLevelGrid.${index}.${name}EdgeValue`)} disabled={readOnly} data-testid={`${name}-performance-level-${index}-edge-value`} />
                </td>
              }
              {
                (fields.length > 1 && !readOnly) && <td>
                  <Button variant="ghost" size="inline" tone="danger" className="text-bigger" icon="bi-trash" onClick={() => remove(index)} data-testid="remove-button" />
                </td>
              }
            </tr>
          ))
        }
      </tbody>
    </table>

    {
      !readOnly && <Button variant="primary" onClick={handleAddRow} data-testid="add-another-button">
        <i className="bi bi-plus bi-bold"></i> {editableTranslate('form.buttom.add_another')}
      </Button>
    }
  </div>;
}

function PerformanceMapResultGrid({ fieldsArray, register, getValues, errors, readOnly = false }) {
  const { fields, append, remove, replace } = fieldsArray;

  const { editableTranslate } = useTranslation();

  const handleAddRow = () => {
    const obj = {
      performanceResultMap: null,
      performanceResultMapValueOrFactor: null
    };

    append(obj);
  };

  const sortDesc = () => {
    const values = getValues("performanceMapResultGrid");

    const sorted = [...values].sort((a, b) => {
      const aVal = Number(formatNumberTo(a.performanceResultMap, '.'));
      const bVal = Number(formatNumberTo(b.performanceResultMap, '.'));

      const aEmpty = isNaN(aVal);
      const bEmpty = isNaN(bVal);

      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      return bVal - aVal;
    });

    replace(sorted);
  };

  return <div>
    <table className="w-auto">
      <thead>
        <tr>
          <th className="pb-2">{editableTranslate('balance_scorecard.performance_result_map')}</th>
          <th className="pl-3 pb-2">{editableTranslate('balance_scorecard.performance_result_map.value_or_factor')}</th>
          {
            (fields.length > 1 && !readOnly) && <th className="pb-2"></th>
          }
        </tr>
      </thead>
      <tbody>
        {
          fields.map((field, index) => (
            <tr key={field.id} data-testid={`row-${field.id}`}>
              <td className="py-1">
                <InputAffix prefix=">=" error={!!errors?.performanceMapResultGrid?.[index]?.performanceResultMap}>
                  <Input type="text" error={!!errors?.performanceMapResultGrid?.[index]?.performanceResultMap} {...register(`performanceMapResultGrid.${index}.performanceResultMap`)} onBlur={sortDesc} readOnly={readOnly} />
                </InputAffix>
              </td>
              <td className="pl-3 py-1">
                <Input type="text" error={!!errors?.performanceMapResultGrid?.[index]?.performanceResultMapValueOrFactor} {...register(`performanceMapResultGrid.${index}.performanceResultMapValueOrFactor`)} readOnly={readOnly} />
              </td>
              {
                (fields.length > 1 && !readOnly) && <td className="pl-3 v-middle">
                  <Button variant="ghost" size="inline" tone="danger" className="text-bigger" icon="bi-trash" onClick={() => remove(index)} data-testid="remove-button" />
                </td>
              }
            </tr>
          ))
        }
      </tbody>
    </table>

    {
      !readOnly && <Button variant="primary" onClick={handleAddRow} data-testid="add-another-button" className="mt-3">
        <i className="bi bi-plus bi-bold"></i> {editableTranslate('form.buttom.add_another')}
      </Button>
    }
  </div>;
}

export default function BalanceScorecard() {

  const { translate, editableTranslate } = useTranslation();

  // initialize popover
  $(function () {
    $('[data-toggle="popover"]').popover({
      html: true,
      trigger: 'hover',
      placement: 'auto'

    })
  });
  const { activeFieldset, setActiveFieldset, helps } = useOutletContext();

  const dispatch = useDispatch();

  const navigate = useNavigate();


  const lang = useSelector(getLang);
  const tenantIsShadow = useSelector(getTenantIsShadow);

  const balanceScorecardData = useSelector(getBalanceScorecardData);
  const balanceScorecardFieldsets = useSelector(getBalanceScorecardFieldsets);
  const overrideReadOnly = useSelector(getBalanceScorecardOverrideReadOnly);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [existingTemplateOptions, setExistingTemplateOptions] = useState([]);
  const [copyTemplate, setCopyTemplate] = useState(null);

  const [systematicViewMode, setSystematicViewMode] = useState(true);

  const [readOnly, setReadOnly] = useState(false);
  const { id } = useParams();
  const [settingRemarks, setSettingRemarks] = useState({});
  const [lockVersion, setLockVersion] = useState(null);
  const [balancedScorecardFormType, setBalancedScorecardFormType] = useState('standard_balanced_scorecard_setting');

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [formData, setFormData] = useState(null);

  const scrollY = useScrollPosition(15);
  const [currentFieldsets, setCurrentFieldsets] = useState([]);

  const [isSystematicError, setIsSystematicError] = useState(false);
  // bumped on each failed systematic validation; the effect scrolls once the error alert has rendered
  const [systematicErrorScroll, setSystematicErrorScroll] = useState(0);

  const systematicErrorRef = useRef(null);
  const formErrorsRef = useRef(null);

  const balancedScorecardFormTypes = {
    standard: 'standard_balanced_scorecard_setting',
    custom: 'custom_balanced_scorecard_setting'
  };

  const decimalSchema = yup
    .string()
    .transform(cv => cv ? cv : '')
    .test('is-decimal', translate(`form.valid_format_decimal_3`), (value) => {
      if (!value) return true;
      return fieldDecimalCheck(value, { decimal: 3 });
    });

  const signedDecimalSchema = yup
    .string()
    .transform(cv => cv ? cv : '')
    .test('is-decimal', translate(`form.valid_format_decimal_3`), (value) => {
      if (!value) return true;

      const normalized = value.startsWith('-') ? value.slice(1) : value;

      if (!normalized) return false;

      return fieldDecimalCheck(normalized, { decimal: 3 });
    });

  const weightSchema = yup
    .string()
    .transform(cv => cv ? cv : '')
    .test('is-decimal', translate(`form.valid_format_decimal_3`), (value) => {
      if (!value) return true;
      return fieldDecimalCheck(value, { decimal: 3 });
    })
    .test('number-range', translate('balance_scorecard.weight_must_be_1_to_100', 'Die Gewichtung muss zwischen 1 und 100 liegen.'), (value) => {
      if (!value) return true;
      const temp = Number(formatNumberTo(value, '.'));
      return temp >= 1 && temp <= 100;
    });

  const schema = yup.object({
    settingName: yup.string().max(255).required().label(translate('balance_scorecard.setting_name')),
    showSettingCopyRadio: yup.boolean().notRequired(),
    howToAppraisal: yup.string().required(),
    shouldHasSubCriteria: yup.boolean().when('howToAppraisal', {
      is: (val) => val !== 'individual_objective_only',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    systematicObjectiveDatagrid: yup.array().of(yup.object().shape({
      id: yup.string().required(),
      systematicObjectiveGridName: yup.string().max(255).required().label(translate('balanced_scorecard.objective_name')),
      systematicObjectiveGridDescription: yup.string().notRequired(),
      systematicObjectiveAgreeObjective: yup.boolean().notRequired(),
      systematicObjectiveDefineWeightingObjective: yup.boolean().notRequired(),
      systematicObjectiveGridWeighted: yup.string().notRequired(),
      weighted: yup.object().shape({
        mode: yup.string().required(),
        value: yup.string().concat(weightSchema).when('mode', {
          is: 'manual',
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired()
        }).label(translate('balanced_scorecard.objective_weighted')),
        share_with: yup.array().of(yup.string()).notRequired()
      })
    })).when(['howToAppraisal', 'shouldHasSubCriteria'], {
      is: (val1, val2) => val1 !== 'individual_objective_only' && val2 === false,
      then: (schema) => schema.min(1).required(),
      otherwise: (schema) => schema.notRequired()
    }),
    systematicObjectiveDatagrid_subCriteria: yup.array().of(yup.object().shape({
      id: yup.string().required(),
      systematicObjectiveGridName: yup.string().max(255).required().label(translate('balanced_scorecard.objective_name')),
      systematicObjectiveAgreeObjective: yup.boolean().notRequired(),
      systematicObjectiveDefineWeightingObjective: yup.boolean().notRequired(),
      systematicObjectiveGridWeighted: yup.string().notRequired(),
      weighted: yup.object().shape({
        mode: yup.string().required(),
        value: yup.string().concat(weightSchema).when('mode', {
          is: 'manual',
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired()
        }).label(translate('balanced_scorecard.objective_weighted'))
      }),
      systematicObjectivesubCriteria: yup.array().of(yup.object().shape({
        id: yup.string().required(),
        systematicSubObjectiveGridName: yup.string().max(255).required().label(translate('balanced_scorecard.objective_name')),
        systematicSubObjectiveGridDescription: yup.string().notRequired(),
        systematicSubObjectiveAgreeObjective: yup.boolean().notRequired(),
        systematicSubObjectiveDefineWeightingObjective: yup.boolean().notRequired(),
        systematicSubObjectiveGridWeighted: yup.string().notRequired(),
        weighted: yup.object().shape({
          mode: yup.string().required(),
          value: yup.string().concat(weightSchema).when('mode', {
            is: 'manual',
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.notRequired()
          }).label(translate('balanced_scorecard.objective_weighted')),
          share_with: yup.array().of(yup.string()).notRequired()
        })
      })).min(1).required()
    })).when(['howToAppraisal', 'shouldHasSubCriteria'], {
      is: (val1, val2) => val1 !== 'individual_objective_only' && val2 === true,
      then: (schema) => schema.min(1).required(),
      otherwise: (schema) => schema.notRequired()
    }),
    individualMandatoryCommentForExtremeRating: yup.boolean().notRequired(),
    individualMandatoryCommentForTeamObjectives: yup.boolean().notRequired(),
    individualMandatoryCommentForExtremeRatingType: yup.string().when('individualMandatoryCommentForExtremeRating', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }).label(translate('balanced_scorecard.mandatory_comment_overall_min')),
    individualMandatoryCommentExtremeRatingMinimum: yup.string().concat(decimalSchema).when('individualMandatoryCommentForExtremeRatingType', {
      is: (val) => val === 'based_on_overall_result',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }).label(translate('balanced_scorecard.mandatory_comment_overall_max')),
    individualMandatoryCommentExtremeRatingMaximum: yup.string().concat(decimalSchema).when('individualMandatoryCommentForExtremeRatingType', {
      is: (val) => val === 'based_on_overall_result',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    hasDifferentEvaluationLevel: yup.boolean().notRequired(),
    generalPerformanceLevelGrid: yup.array().of(yup.object().shape({
      generalLevel: yup.string().required().label(translate('balanced_scorecard.performance_level')),
      generalValue: yup.string().concat(decimalSchema).required().label(translate('balanced_scorecard.performance_level_value')),
      generalDescription: yup.string().notRequired(),
      generalFulfillment: yup.boolean().notRequired(),
      generalEdgeValue: yup.boolean().notRequired()
    })).when(['howToAppraisal', 'hasDifferentEvaluationLevel'], {
      is: (val1, val2) => val1 === 'individual_and_systematic_objective' && val2 === false,
      then: (schema) => schema.min(1).required(),
      otherwise: (schema) => schema.notRequired()
    }),
    individualObjectivePerformanceLevelGrid: yup.array().of(yup.object().shape({
      individualObjectiveLevel: yup.string().required().label(translate('balanced_scorecard.performance_level')),
      individualObjectiveValue: yup.string().concat(decimalSchema).required().label(translate('balanced_scorecard.performance_level_value')),
      individualObjectiveDescription: yup.string().notRequired(),
      individualObjectiveFulfillment: yup.boolean().notRequired(),
      individualObjectiveEdgeValue: yup.boolean().notRequired()
    })).when(['howToAppraisal', 'hasDifferentEvaluationLevel'], {
      is: (val1, val2) => val1 === 'individual_objective_only' || (val1 === 'individual_and_systematic_objective' && val2 === true),
      then: (schema) => schema.min(1).required(),
      otherwise: (schema) => schema.notRequired()
    }),
    individualNumberOfDecimalPlaces: yup.number().when('howToAppraisal', {
      is: (val) => val === 'individual_objective_only' || val === 'individual_and_systematic_objective',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    systematicMandatoryCommentForExtremeRating: yup.boolean().notRequired(),
    systematicMandatoryCommentForExtremeRatingType: yup.string().when('systematicMandatoryCommentForExtremeRating', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    systematicMandatoryCommentExtremeRatingMinimum: yup.string().concat(decimalSchema).when('systematicMandatoryCommentForExtremeRatingType', {
      is: (val) => val === 'based_on_overall_result',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }).label(translate('balanced_scorecard.mandatory_comment_overall_min')),
    systematicMandatoryCommentExtremeRatingMaximum: yup.string().concat(decimalSchema).when('systematicMandatoryCommentForExtremeRatingType', {
      is: (val) => val === 'based_on_overall_result',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }).label(translate('balanced_scorecard.mandatory_comment_overall_max')),
    systematicObjectivePerformanceLevelGrid: yup.array().of(yup.object().shape({
      systematicObjectiveLevel: yup.string().required().label(translate('balanced_scorecard.performance_level')),
      systematicObjectiveValue: yup.string().concat(decimalSchema).required().label(translate('balanced_scorecard.performance_level_value')),
      systematicObjectiveDescription: yup.string().notRequired(),
      systematicObjectiveFulfillment: yup.boolean().notRequired(),
      systematicObjectiveEdgeValue: yup.boolean().notRequired()
    })).when(['howToAppraisal', 'hasDifferentEvaluationLevel'], {
      is: (val1, val2) => val1 === 'systematic_objective_only' || (val1 === 'individual_and_systematic_objective' && val2 === true),
      then: (schema) => schema.min(1).required(),
      otherwise: (schema) => schema.notRequired()
    }),
    systematicNumberOfDecimalPlaces: yup.number().when(['howToAppraisal', 'hasDifferentEvaluationLevel'], {
      is: (val1, val2) => val1 === 'systematic_objective_only' || (val1 === 'individual_and_systematic_objective' && val2 === true),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    performanceResultCalculation: yup.string().required(),
    performanceResultRounding: yup.string().required(),
    performanceResultNumberOfDecimalPlaces: yup.number().required(),
    allowChangesOfResult: yup.boolean().notRequired(),
    formOfPerformanceMapResult: yup.string().when('allowChangesOfResult', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    manualConversionLowerLimit: yup.string().concat(signedDecimalSchema).when('allowChangesOfResult', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    manualConversionUpperLimit: yup.string().concat(signedDecimalSchema).when('allowChangesOfResult', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    shouldConvertResultToPayoutPoints: yup.boolean().notRequired(),
    resultConversionType: yup.string().when('shouldConvertResultToPayoutPoints', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    performanceMapResultGrid: yup.array().of(yup.object().shape({
      performanceResultMap: yup.string().concat(decimalSchema).required().label(translate('balanced_scorecard.performance_map_result')),
      performanceResultMapValueOrFactor: yup.string().concat(decimalSchema).required().label(translate('balanced_scorecard.performance_map_value')),
    })).when('shouldConvertResultToPayoutPoints', {
      is: true,
      then: (schema) => schema.min(1).required(),
      otherwise: (schema) => schema.notRequired()
    }),
    showBalancedScorecardNotes: yup.boolean().notRequired(),
    balancedScorecardNotes: yup.string().notRequired(),
    showRatingNotes: yup.boolean().notRequired(),
    ratingNotes: yup.string().notRequired(),
    completedBehaviour: yup.string().required(),
    reportSignature: yup.string().required()
  });

  const { register, watch, control, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      showSettingCopyRadio: false,
      howToAppraisal: 'systematic_objective_only',
      shouldHasSubCriteria: false,
      systematicObjectiveDatagrid: [],
      systematicObjectiveDatagrid_subCriteria: [],
      individualMandatoryCommentForExtremeRating: false,
      individualMandatoryCommentForTeamObjectives: false,
      individualMandatoryCommentForExtremeRatingType: 'based_on_single_objective',
      hasDifferentEvaluationLevel: false,
      generalPerformanceLevelGrid: [],
      individualObjectivePerformanceLevelGrid: [],
      individualNumberOfDecimalPlaces: 0,
      systematicMandatoryCommentForExtremeRating: false,
      systematicMandatoryCommentForExtremeRatingType: 'based_on_single_objective',
      systematicObjectivePerformanceLevelGrid: [],
      systematicNumberOfDecimalPlaces: 0,
      performanceResultCalculation: 'average',
      performanceResultRounding: 'commercially',
      performanceResultNumberOfDecimalPlaces: 3,
      allowChangesOfResult: false,
      formOfPerformanceMapResult: 'fixed_value',
      shouldConvertResultToPayoutPoints: false,
      resultConversionType: 'assign_value',
      performanceMapResultGrid: [],
      showBalancedScorecardNotes: false,
      showRatingNotes: false,
      completedBehaviour: 'no_changes_possible',
      reportSignature: 'no_signature'
    }
  });

  const systematicArray = useFieldArray({
    control,
    name: "systematicObjectiveDatagrid"
  });

  const systematicSubArray = useFieldArray({
    control,
    name: "systematicObjectiveDatagrid_subCriteria"
  });

  const generalArray = useFieldArray({
    control,
    name: "generalPerformanceLevelGrid"
  });

  const individualObjectiveArray = useFieldArray({
    control,
    name: "individualObjectivePerformanceLevelGrid"
  });

  const systematicObjectiveArray = useFieldArray({
    control,
    name: "systematicObjectivePerformanceLevelGrid"
  });

  const performanceMapArray = useFieldArray({
    control,
    name: "performanceMapResultGrid"
  });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const watchAll = watch();

  const watchHowToAppraisal = watch('howToAppraisal');
  const watchShouldHasSubCriteria = watch('shouldHasSubCriteria');
  const watchHasDifferentEvaluationLevel = watch('hasDifferentEvaluationLevel');
  const watchShouldConvertResultToPayoutPoints = watch('shouldConvertResultToPayoutPoints');

  useEffect(() => {
    if (watchHowToAppraisal === 'individual_objective_only') {
      setValue('systematicObjectiveDatagrid', []);
      setValue('systematicObjectiveDatagrid_subCriteria', []);
    } else {
      if (watchShouldHasSubCriteria) {
        setValue('systematicObjectiveDatagrid', []);

        if (getValues('systematicObjectiveDatagrid_subCriteria')?.length < 1) {
          setValue('systematicObjectiveDatagrid_subCriteria', [{
            id: crypto.randomUUID(),
            systematicObjectiveGridName: null,
            systematicObjectiveAgreeObjective: false,
            systematicObjectiveDefineWeightingObjective: false,
            systematicObjectiveGridWeighted: null,
            weighted: {
              mode: 'by_evaluator',
              value: null
            },
            systematicObjectivesubCriteria: [{
              id: crypto.randomUUID(),
              systematicSubObjectiveGridName: null,
              systematicSubObjectiveGridDescription: null,
              systematicSubObjectiveAgreeObjective: false,
              systematicSubObjectiveDefineWeightingObjective: false,
              systematicSubObjectiveGridWeighted: null,
              weighted: {
                mode: 'by_evaluator',
                value: null,
                share_with: []
              }
            }]
          }]);
        }
      } else {
        setValue('systematicObjectiveDatagrid_subCriteria', []);

        if (getValues('systematicObjectiveDatagrid')?.length < 1) {
          setValue('systematicObjectiveDatagrid', [{
            id: crypto.randomUUID(),
            systematicObjectiveGridName: null,
            systematicObjectiveGridDescription: null,
            systematicObjectiveAgreeObjective: false,
            systematicObjectiveDefineWeightingObjective: false,
            systematicObjectiveGridWeighted: null,
            weighted: {
              mode: 'by_evaluator',
              value: null,
              share_with: []
            }
          }]);
        }
      }
    }
  }, [watchHowToAppraisal, watchShouldHasSubCriteria]);

  useEffect(() => {
    if (watchHowToAppraisal !== 'individual_and_systematic_objective' || watchHasDifferentEvaluationLevel === true) {
      setValue('generalPerformanceLevelGrid', []);
    } else if (getValues('generalPerformanceLevelGrid')?.length < 1) {
      setValue('generalPerformanceLevelGrid', [{
        generalLevel: null,
        generalValue: null,
        generalDescription: null,
        generalFulfillment: false,
        generalEdgeValue: false
      }]);
    }

    if (watchHowToAppraisal === 'systematic_objective_only' || (watchHowToAppraisal === 'individual_and_systematic_objective' && watchHasDifferentEvaluationLevel === false)) {
      setValue('individualObjectivePerformanceLevelGrid', []);
    } else if (getValues('individualObjectivePerformanceLevelGrid')?.length < 1) {
      setValue('individualObjectivePerformanceLevelGrid', [{
        individualObjectiveLevel: null,
        individualObjectiveValue: null,
        individualObjectiveDescription: null,
        individualObjectiveFulfillment: false,
        individualObjectiveEdgeValue: false
      }]);
    }

    if (watchHowToAppraisal === 'individual_objective_only' || (watchHowToAppraisal === 'individual_and_systematic_objective' && watchHasDifferentEvaluationLevel === false)) {
      setValue('systematicObjectivePerformanceLevelGrid', []);
    } else if (getValues('systematicObjectivePerformanceLevelGrid')?.length < 1) {
      setValue('systematicObjectivePerformanceLevelGrid', [{
        systematicObjectiveLevel: null,
        systematicObjectiveValue: null,
        systematicObjectiveDescription: null,
        systematicObjectiveFulfillment: false,
        systematicObjectiveEdgeValue: false
      }]);
    }
  }, [watchHowToAppraisal, watchHasDifferentEvaluationLevel]);

  useEffect(() => {
    if (watchShouldConvertResultToPayoutPoints === false) {
      setValue('performanceMapResultGrid', []);
    } else if (getValues('performanceMapResultGrid')?.length < 1) {
      setValue('performanceMapResultGrid', [{
        performanceResultMap: null,
        performanceResultMapValueOrFactor: null
      }]);
    }
  }, [watchShouldConvertResultToPayoutPoints]);

  const helpsObj = useMemo(() => {
    const obj = {};
    if (helps) {
      for (const item of helps) {
        obj[item.key] = item;
      }
    }
    return obj;
  }, [helps]);

  const isCustom = useMemo(() => {
    if (balancedScorecardFormType && balancedScorecardFormType === balancedScorecardFormTypes.custom) return true;
    return false;
  }, [balancedScorecardFormType]);

  useEffect(() => {
    if (copyTemplate?.value) {
      fetchByHashId(copyTemplate?.value, true);
    }
  }, [copyTemplate]);

  useEffect(() => {
    if (balanceScorecardData?.data) {
      const formatted = structuredClone(balanceScorecardData?.data);
      if (formatted?.individualNumberOfDecimalPlaces) {
        formatted.individualNumberOfDecimalPlaces = Number(formatted?.individualNumberOfDecimalPlaces);
      }
      if (formatted?.systematicNumberOfDecimalPlaces) {
        formatted.systematicNumberOfDecimalPlaces = Number(formatted?.systematicNumberOfDecimalPlaces);
      }
      if (formatted?.performanceResultNumberOfDecimalPlaces) {
        formatted.performanceResultNumberOfDecimalPlaces = Number(formatted?.performanceResultNumberOfDecimalPlaces);
      }

      reset(formatted);

      dispatch(updatePrefilledData({}));
    }
  }, [balanceScorecardData]);

  // reset override when entering/leaving the page
  useEffect(() => {
    dispatch(setOverrideReadOnly(false));
    return () => dispatch(setOverrideReadOnly(false));
  }, [dispatch]);

  useEffect(() => {
    setActiveFieldset(null);
    fetchSettings();
  }, []);

  useEffect(() => {
    const arr = ['setting', 'performance_evaluation', 'systematic_objective_settings', 'performance_levels', 'other_settings'];
    dispatch(updateBalanceScorecardFieldsets(arr.map(a => {
      let show = true;
      if (a === 'systematic_objective_settings' && watchHowToAppraisal === 'individual_objective_only') show = false;

      if (isCustom && !['setting', 'other_settings'].includes(a)) show = false;

      return {
        key: a,
        label: translate(`balance_scorecard.${a}.title`, a),
        show
      };
    })));
  }, [translate, watchHowToAppraisal, isCustom]);

  const fetchSettings = async () => {
    try {
      const { data, status } = await api.get(`/settings/balanced-scorecards`);
      if (status === 200) {
        setExistingTemplateOptions(data.data
          .filter(d => d?.form_definition?.type !== 'custom_balanced_scorecard_setting')
          .map(d => ({ value: d.hash_id, label: d.name }))
        );
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch balanced scorecard settings'));
      Sentry.captureException(err);
    }
  };

  const handleSaveRemarks = (id, content) => {
    if (!tenantIsShadow) return;

    const newSettingRemarks = { ...settingRemarks, [id]: content };
    setSettingRemarks(newSettingRemarks);
  };

  const fetchByHashId = async (hashId, copy = false) => {
    try {
      const { data, status } = await api.get(`/settings/balanced-scorecards/${hashId}?showSetting=true`);
      if (status === 200) {
        setBalancedScorecardFormType(data.form_definition.type);

        const newData = { ...data.settings };
        if (copy) {
          newData.settingName = watchAll?.settingName;
          newData.showSettingCopyRadio = true;
        } else {
          newData.showSettingCopyRadio = false;
        }

        for (const k in newData) {
          if (k.includes('NumberOfDecimalPlaces')) {
            newData[k] = Number(newData[k]);
          }
        }

        if (!newData.hasOwnProperty('individualMandatoryCommentForTeamObjectives')) {
          newData.individualMandatoryCommentForTeamObjectives = false;
        }

        reset(newData);

        if(!copy) setLockVersion(data.lock_version);
        setSettingRemarks(data.setting_remarks);

        injectRemarksView(data.setting_remarks);
        if (!copy) {
          scrollToTop();
          setReadOnly(data.in_use ?? false);
        }
      }
    } catch (err) {
      setError(errorMessage(err, 'get balanced scorecard setting'));
      Sentry.captureException(err);
    }
  };

  const injectRemarksView = (settingRemarks) => {
    if (!settingRemarks || tenantIsShadow) return;
    for (const k in settingRemarks) {
      const element = document.querySelector(`label[id$="${k}"]`);
      if (element) {
        const remarksView = remarksViewButtonString(k, 'Remarks', settingRemarks[k]);

        // remove remarks button if it exists
        const remarksButton = element.querySelector(`[data-id="${k}"]`);
        if (remarksButton) remarksButton.remove();

        element.insertAdjacentHTML('beforeend', remarksView);
      }
    }
  }

  // Allow internal tools override to force editable mode
  useEffect(() => {
    if (overrideReadOnly) {
      setReadOnly(false);
    }
  }, [overrideReadOnly]);

  useEffect(() => {
    if (id) fetchByHashId(id);
  }, [id]);

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
  };

  useEffect(() => {
    const fieldsets = balanceScorecardFieldsets.map(fieldset => {
      let top = -1;
      if (fieldset.show) {
        top = document.querySelector(`#fieldset-${fieldset.key}`)?.getBoundingClientRect().top - document.querySelector('.content-card')?.getBoundingClientRect().top;
      }

      return { ...fieldset, top: Math.floor(top) };
    });

    fieldsets.sort(function (a, b) {
      return b.top - a.top;
    });

    setCurrentFieldsets(fieldsets);
  }, [balanceScorecardFieldsets]);

  useEffect(() => {
    for (let i = 0; i < currentFieldsets.length; i++) {
      if (currentFieldsets[i].show
        && currentFieldsets[i].top !== -1
        && scrollY >= (currentFieldsets[i].top - 20)
        && activeFieldset !== currentFieldsets[i].key) {
        if (i === 0 || (i > 0 && scrollY < currentFieldsets[i - 1].top)) {
          setActiveFieldset(currentFieldsets[i].key);
          break;
        }
      }
    }
  }, [scrollY]);

  const handlePreview = () => {
    setFormData(watchAll);
    setShowPreviewModal(true);
  };

  const validateSystematicAuto = (errs, arr, agreeKey = 'systematicObjectiveAgreeObjective') => {
    let allAuto = true;
    let hasAuto = false;
    let hasOptional = false;
    let totalWeight = 0;
    const shareWith = [];
    for (const item of arr) {
      if (item?.weighted?.mode !== 'auto') allAuto = false;
      else hasAuto = true;

      if (!item?.[agreeKey] || item?.weighted?.mode === 'by_evaluator') hasOptional = true;

      if (item?.weighted?.mode === 'manual') {
        totalWeight += parseFloat(formatNumberTo(item?.weighted?.value, '.'));
        if (item?.weighted?.share_with?.length > 0) {
          shareWith.push(...item?.weighted?.share_with);
        }
      }
    }

    if (!allAuto && hasAuto) {
      if (totalWeight === 0) {
        errs.push(translate('balance_scorecard.automatic_at_least_one_weighted_error'));
      } else {
        for (const item of arr) {
          if (item?.weighted?.mode === 'auto') {
            if (!shareWith.includes(item?.id)) {
              errs.push(translate('balance_scorecard.automatic_requires_share_with'));
              break;
            }
          }
        }
      }
    }

    if (!allAuto && !hasOptional && totalWeight !== 100) {
      errs.push(translate('evaluation.validation.total_weight_systematic'));
    };
  };

  const validateAllSystematic = (data) => {
    const errs = [];

    if (data.howToAppraisal !== 'individual_objective_only') {
      if (data.shouldHasSubCriteria === false) {
        validateSystematicAuto(errs, data.systematicObjectiveDatagrid);
      } else {
        let hasOptional = false;
        let totalWeight = 0;
        for (const item of data.systematicObjectiveDatagrid_subCriteria) {
          if (!item.systematicObjectiveAgreeObjective || item?.weighted?.mode === 'by_evaluator') hasOptional = true;

          if (item?.weighted?.mode === 'manual') {
            totalWeight += parseFloat(formatNumberTo(item?.weighted?.value, '.'));
          }

          validateSystematicAuto(errs, item.systematicObjectivesubCriteria, 'systematicSubObjectiveAgreeObjective');
        }

        if (!hasOptional && totalWeight !== 100) {
          errs.push(translate('evaluation.validation.total_weight_systematic'));
        };
      }
    }

    return errs;
  };

  const onSubmit = async (data) => {
    const errs = validateAllSystematic(data);
    if (errs.length > 0) {
      setError('<ul class="pl-3 mb-0">' + errs.map(e => `<li>${e}</li>`).join('') + '</ul>');
      setIsSystematicError(true);
      setSystematicErrorScroll(n => n + 1);
      return;
    } else {
      setIsSystematicError(false);
    }

    setLoading(true);

    try {
      delete data?.settingRemarks;
      delete data?.lockVersion;

      const formData = {
        name: data.settingName,
        lock_version: lockVersion || null,
        form_definition: balancedScorecardFormType,
        settings: data
      };

      if (tenantIsShadow) {
        formData.setting_remarks = settingRemarks;
      }

      if (id) formData.hash_id = id;
      const { status } = await api.post(`/settings/balanced-scorecards`, structuredClone(formData));
      if (status === 200) {
        dispatch(fetchBalanceScorecardList());

        toast.success(translate('form.success.saved'));

        navigate('/settings/balance-scorecard');
      }
    } catch (err) {
      scrollInto('.formio-form');

      if (err.response?.status === 409) {
        toast.error(translate('form.error.conflicted'));
      }

      setError(errorMessage(err, 'save balanced scorecard setting', false));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const collectErrors = (err) => {
    let messages = [];

    if (!err) return messages;

    if (Array.isArray(err)) {
      err.forEach((e) => {
        messages.push(...collectErrors(e));
      });
      return messages;
    }

    if (typeof err === 'object') {
      for (const k in err) {
        if (Array.isArray(err[k])) {
          messages.push(...collectErrors(err[k]));
        } else if (err[k]?.message) {
          messages.push({
            message: err[k].message,
            ref: err[k]?.ref
          });
        } else if (typeof err[k] === 'object') {
          messages.push(...collectErrors(err[k]));
        }
      }
    }

    return messages;
  };

  const handleErrorClick = (e, msg) => {
    e?.currentTarget?.blur();
    e?.preventDefault();

    const el = msg?.ref;
    if (el) {
      let offset = 100;
      if (formErrorsRef?.current) {
        offset += formErrorsRef?.current?.offsetHeight;
      }
      let y = el.getBoundingClientRect().top + (window.scrollY - (navHeight() ?? 0)) - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (systematicErrorScroll) handleSystematicErrorClick();
  }, [systematicErrorScroll]);

  const handleSystematicErrorClick = () => {
    if (!isSystematicError) return;

    const el = document.querySelector('#systematic-table-div');
    let offset = 100;
    if (systematicErrorRef?.current) {
      offset += systematicErrorRef?.current?.offsetHeight;
    }
    let y = el.getBoundingClientRect().top + (window.scrollY - (navHeight() ?? 0)) - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card formio-form ${lang + '-lang'}`}>
          <h4 className="mb-3">
            {editableTranslate("global.menu.settings.balance_scorecard")}
          </h4>
          {
            (Object.keys(errors).length === 0 && error) && <div ref={systematicErrorRef} className={isSystematicError ? `sticky z-2 pt-3 bg-white` : ''}>
              <div data-testid="scroll-to-error" onClick={handleSystematicErrorClick} className={isSystematicError ? `cursor-pointer` : ''}>
                <ErrorAlert error={error} isHtml={true} />
              </div>
            </div>
          }

          {
            Object.keys(errors).length > 0 && <div ref={formErrorsRef} className="sticky z-2 pt-3 bg-white">
              <div className="alert alert-danger">
                <ul data-testid="error-list" className="pl-3 mb-0">
                  {
                    collectErrors(errors).map((msg, i) => <li key={i}>
                      <a href="/#" className="color-inherit" onClick={(e) => handleErrorClick(e, msg)}>{msg.message}</a>
                    </li>)
                  }
                </ul>
              </div>
            </div>
          }

          {
            (readOnly && !overrideReadOnly) && <div className="alert alert-warning" role="alert">
              {editableTranslate('balance_scorecard.setting.in_use_info', 'Die Leistungskarte ist Beschäftigten zugewiesen und kann daher nicht geändert werden.')}
            </div>
          }
          {
            isCustom && <SettingCard setting={watchAll} />
          }

          <form className="settings-form settings-lk" onSubmit={handleSubmit(onSubmit)}>
            <div className="section-heading-div" id="fieldset-setting">
              <p className="section-title">{editableTranslate('balance_scorecard.setting.title')}</p>
              <p className="mt-2">{editableTranslate('balance_scorecard.setting.description')} {renderHelpIcon(helpsObj, 'settingDescription')}</p>
            </div>

            <div className="form-group gray-box">
              <label className="question-label">{editableTranslate('balance_scorecard.setting_name')} {renderHelpIcon(helpsObj, 'settingName')}</label>
              <div className="options-div">
                <Field error={errors?.settingName?.message}>
                  <Input type="text" error={!!errors?.settingName} className="col-lg-6" maxLength="255" {...register('settingName')} readOnly={readOnly} data-testid="setting-name-input" />
                </Field>
              </div>
            </div>

            {
              !id && <>
                <div className="form-group gray-box">
                  <label className="question-label">{editableTranslate('balance_scorecard.setting_copy_radio')} {renderHelpIcon(helpsObj, 'settingCopyRadio')}</label>
                  <div className="options-div">
                    <InlineBoolean control={control} editableTranslate={editableTranslate} name="showSettingCopyRadio" readOnly={readOnly} />
                  </div>
                </div>

                {
                  watchAll?.showSettingCopyRadio === true && <div className="form-group gray-box">
                    <label className="question-label">{editableTranslate('balance_scorecard.setting_copy_select')} {renderHelpIcon(helpsObj, 'settingCopySelect')}</label>
                    <div className="options-div">
                      <div style={{ display: 'contents' }} data-testid="copy-from-setting-select">
                        <Select className="p-0 col-lg-6" placeholder={translate('form.select_placeholder')} options={existingTemplateOptions} value={copyTemplate} onChange={setCopyTemplate} />
                      </div>
                    </div>
                  </div>
                }
              </>
            }

            {
              !isCustom && <>
                <div className="section-heading-div" id="fieldset-performance_evaluation">
                  <p className="section-title">{editableTranslate('balance_scorecard.performance_evaluation.title')}</p>
                  <p className="mt-2">{editableTranslate('balance_scorecard.performance_evaluation.description')} {renderHelpIcon(helpsObj, 'performanceEvaluationDescription')}</p>
                </div>

                <div className="form-group gray-box">
                  <label className="question-label">{editableTranslate('balance_scorecard.how_to_appraisal')} {renderHelpIcon(helpsObj, 'howToAppraisal')}</label>
                  <div className="options-div">
                    <label className="d-flex align-items-center">
                      <input type="radio" value="individual_objective_only" {...register('howToAppraisal')} disabled={readOnly} data-testid="how-to-appraisal-individual-objective-only-radio" /> {editableTranslate('balance_scorecard.how_to_appraisal.individual_objective_only')}
                    </label>
                    <label className="d-flex align-items-center">
                      <input type="radio" value="systematic_objective_only" {...register('howToAppraisal')} disabled={readOnly} data-testid="how-to-appraisal-systematic-objective-only-radio" /> {editableTranslate('balance_scorecard.how_to_appraisal.systematic_objective_only')}
                    </label>
                    <label className="d-flex align-items-center">
                      <input type="radio" value="individual_and_systematic_objective" {...register('howToAppraisal')} disabled={readOnly} data-testid="how-to-appraisal-individual-and-systematic-objective-radio" /> {editableTranslate('balance_scorecard.how_to_appraisal.individual_and_systematic_objective')}
                    </label>
                  </div>
                </div>

                {
                  watchAll?.howToAppraisal !== 'individual_objective_only' && <>
                    <div className="section-heading-div d-flex align-items-center justify-content-between mb-3" id="fieldset-systematic_objective_settings">
                      <div>
                        <p className="section-title">{editableTranslate('balance_scorecard.systematic_objective_settings.title')}</p>
                        <p className="mt-2 mb-0">{editableTranslate('balance_scorecard.systematic_objective_setting.description')} {renderHelpIcon(helpsObj, 'systematicObjectiveSettingDescription')}</p>
                      </div>
                      {
                        (id && systematicViewMode && !readOnly) && <div>
                          <Button variant="secondary" onClick={() => setSystematicViewMode(false)} data-testid="edit-button"><i className="bi bi-pencil-square"></i> {editableTranslate('global.edit')}</Button>
                        </div>
                      }
                    </div>

                    {
                      (id && systematicViewMode) ? <SystematicView data={watchAll} setValue={setValue} getValues={getValues} readOnly={readOnly} /> :
                        <>
                          <div className="form-group gray-box">
                            <label className="question-label">{editableTranslate('balance_scorecard.should_has_sub_criteria')} {renderHelpIcon(helpsObj, 'shouldHasSubCriteria')}</label>
                            <div className="options-div">
                              <InlineBoolean control={control} editableTranslate={editableTranslate} name="shouldHasSubCriteria" readOnly={readOnly} />
                            </div>
                          </div>

                          <div id="systematic-table-div" className="gray-box p-1-2">
                            {
                              watchAll?.shouldHasSubCriteria === true ? <SystematicSubPrimary
                                fieldsArray={systematicSubArray}
                                control={control} register={register} setValue={setValue} getValues={getValues} watch={watch} watchAll={watchAll} errors={errors} readOnly={readOnly} helpsObj={helpsObj} />
                                : <Systematic
                                  fieldsArray={systematicArray}
                                  control={control} register={register} setValue={setValue} getValues={getValues} watch={watch} watchAll={watchAll} errors={errors} readOnly={readOnly} helpsObj={helpsObj} />
                            }
                          </div>
                        </>
                    }

                  </>
                }

                <div className="section-heading-div" id="fieldset-performance_levels">
                  <p className="section-title">{editableTranslate('balance_scorecard.performance_levels.title')}</p>
                  <p className="mt-2">{editableTranslate('balance_scorecard.performance_levels.description')} {renderHelpIcon(helpsObj, 'performanceLevelsDescription')}</p>
                </div>

                {
                  watchAll?.howToAppraisal === 'individual_and_systematic_objective' && <div className="form-group gray-box">
                    <label className="question-label">{editableTranslate('balance_scorecard.has_different_evaluation_level')} {renderHelpIcon(helpsObj, 'hasDifferentEvaluationLevel')}</label>
                    <div className="options-div">
                      <InlineBoolean control={control} editableTranslate={editableTranslate} name="hasDifferentEvaluationLevel" readOnly={readOnly} />
                    </div>
                  </div>
                }

                <div className="row">
                  {
                    watchAll?.howToAppraisal !== 'systematic_objective_only' && <div className={watchAll?.howToAppraisal === 'individual_and_systematic_objective' && watchAll?.hasDifferentEvaluationLevel === true ? `col-lg-6` : 'col'}>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form-group gray-box">
                            <label className="question-label">{editableTranslate('balance_scorecard.mandatory_comment_extreme_rating')} {renderHelpIcon(helpsObj, 'individualMandatoryCommentForExtremeRating')}</label>
                            <div className="options-div">
                              <InlineBoolean control={control} editableTranslate={editableTranslate} name="individualMandatoryCommentForExtremeRating" readOnly={readOnly} />
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form-group gray-box">
                            <label className="question-label">{editableTranslate('balance_scorecard.mandatory_comment_for_team_objectives')}</label>
                            <div className="options-div">
                              <InlineBoolean control={control} editableTranslate={editableTranslate} name="individualMandatoryCommentForTeamObjectives" readOnly={readOnly} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {
                        watchAll?.individualMandatoryCommentForExtremeRating === true && <div className="form-group gray-box">
                          <label className="question-label">{editableTranslate('balance_scorecard.mandatory_comment_extreme_rating_sub')} {renderHelpIcon(helpsObj, 'individualMandatoryCommentExtreme-label')}</label>
                          <div className="options-div">
                            <label className="d-flex align-items-center">
                              <input type="radio" value="based_on_single_objective" {...register('individualMandatoryCommentForExtremeRatingType')} disabled={readOnly} data-testid="individual-mandatory-comment-for-extreme-rating-type-based-on-single-objective-radio" /> {editableTranslate('balance_scorecard.based_on_single_objective')}
                            </label>
                            <label className="d-flex align-items-center">
                              <input type="radio" value="based_on_overall_result" {...register('individualMandatoryCommentForExtremeRatingType')} disabled={readOnly} data-testid="individual-mandatory-comment-for-extreme-rating-type-based-on-overall-result-radio" /> {editableTranslate('balance_scorecard.based_on_overall_result')}
                            </label>

                            {
                              watchAll?.individualMandatoryCommentForExtremeRatingType === 'based_on_overall_result' && <div className="mt-3">
                                <div className="d-flex flex-wrap">
                                  <InputAffix className="w-auto flex-nowrap mr-3" prefix={editableTranslate('form.min')} error={!!errors?.individualMandatoryCommentExtremeRatingMinimum}>
                                    <Input type="text" className="input-w-80 ml-2" style={{ flex: "none" }} error={!!errors?.individualMandatoryCommentExtremeRatingMinimum} {...register('individualMandatoryCommentExtremeRatingMinimum')} readOnly={readOnly} data-testid="individual-mandatory-comment-extreme-rating-minimum-input" />
                                  </InputAffix>
                                  <InputAffix className="w-auto flex-nowrap" prefix={editableTranslate('form.max')} error={!!errors?.individualMandatoryCommentExtremeRatingMaximum}>
                                    <Input type="text" className="input-w-80 ml-2" style={{ flex: "none" }} error={!!errors?.individualMandatoryCommentExtremeRatingMaximum} {...register('individualMandatoryCommentExtremeRatingMaximum')} readOnly={readOnly} data-testid="individual-mandatory-comment-extreme-rating-maximum-input" />
                                  </InputAffix>
                                </div>
                                <p className="mb-0 mt-1">{editableTranslate('balance_scorecard.extreme_based_on_overall_result_text_tooltip')} {renderHelpIcon(helpsObj, 'extreme_based_on_overall_result_text_tooltip')}</p>
                              </div>
                            }
                          </div>
                        </div>
                      }

                      <div className="gray-box mb-4 p-1-2">
                        <PerformanceLevelGrid
                          fieldsArray={watchAll?.howToAppraisal === 'individual_and_systematic_objective' && watchAll?.hasDifferentEvaluationLevel !== true ? generalArray : individualObjectiveArray}
                          name={watchAll?.howToAppraisal === 'individual_and_systematic_objective' && watchAll?.hasDifferentEvaluationLevel !== true ? 'general' : 'individualObjective'}
                          showMandatory={watchAll?.individualMandatoryCommentForExtremeRating === true && watchAll?.individualMandatoryCommentForExtremeRatingType === 'based_on_single_objective'}
                          control={control} register={register} errors={errors} readOnly={readOnly} helpsObj={helpsObj} />
                      </div>

                      <div className="form-group gray-box">
                        <label className="question-label">{editableTranslate('balance_scorecard.number_of_decimal_places')} {renderHelpIcon(helpsObj, 'individualNumberOfDecimalPlaces')}</label>
                        <div className="options-div">
                          <DecimalPlaces name="individualNumberOfDecimalPlaces" control={control} editableTranslate={editableTranslate} readOnly={readOnly} />
                        </div>
                      </div>
                    </div>
                  }
                  {
                    (watchAll?.howToAppraisal === 'systematic_objective_only' || (watchAll?.howToAppraisal === 'individual_and_systematic_objective' && watchAll?.hasDifferentEvaluationLevel === true)) && <div className={watchAll?.howToAppraisal === 'individual_and_systematic_objective' ? `col-lg-6` : 'col'}>
                      <div className="form-group gray-box">
                        <label className="question-label">{editableTranslate('balance_scorecard.mandatory_comment_extreme_rating')} {renderHelpIcon(helpsObj, 'systematicMandatoryCommentForExtremeRating')}</label>
                        <div className="options-div">
                          <InlineBoolean control={control} editableTranslate={editableTranslate} name="systematicMandatoryCommentForExtremeRating" readOnly={readOnly} />
                        </div>
                      </div>

                      {
                        watchAll?.systematicMandatoryCommentForExtremeRating === true && <div className="form-group gray-box">
                          <label className="question-label">{editableTranslate('balance_scorecard.mandatory_comment_extreme_rating_sub')}  {renderHelpIcon(helpsObj, 'systematicMandatoryCommentExtreme-label')}</label>
                          <div className="options-div">
                            <label className="d-flex align-items-center">
                              <input type="radio" value="based_on_single_objective" {...register('systematicMandatoryCommentForExtremeRatingType')} disabled={readOnly} data-testid="systematic-mandatory-comment-for-extreme-rating-type-based-on-single-objective-radio" /> {editableTranslate('balance_scorecard.based_on_single_objective')}
                            </label>
                            <label className="d-flex align-items-center">
                              <input type="radio" value="based_on_overall_result" {...register('systematicMandatoryCommentForExtremeRatingType')} disabled={readOnly} data-testid="systematic-mandatory-comment-for-extreme-rating-type-based-on-overall-result-radio" /> {editableTranslate('balance_scorecard.based_on_overall_result')}
                            </label>

                            {
                              watchAll?.systematicMandatoryCommentForExtremeRatingType === 'based_on_overall_result' && <div className="mt-3">
                                <div className="d-flex flex-wrap">
                                  <InputAffix className="w-auto flex-nowrap mr-3" prefix={editableTranslate('form.min')} error={!!errors?.systematicMandatoryCommentExtremeRatingMinimum}>
                                    <Input type="text" className="input-w-80 ml-2" style={{ flex: "none" }} error={!!errors?.systematicMandatoryCommentExtremeRatingMinimum} {...register('systematicMandatoryCommentExtremeRatingMinimum')} readOnly={readOnly} data-testid="systematic-mandatory-comment-extreme-rating-minimum-input" />
                                  </InputAffix>
                                  <InputAffix className="w-auto flex-nowrap" prefix={editableTranslate('form.max')} error={!!errors?.systematicMandatoryCommentExtremeRatingMaximum}>
                                    <Input type="text" className="input-w-80 ml-2" style={{ flex: "none" }} error={!!errors?.systematicMandatoryCommentExtremeRatingMaximum} {...register('systematicMandatoryCommentExtremeRatingMaximum')} readOnly={readOnly} data-testid="systematic-mandatory-comment-extreme-rating-maximum-input" />
                                  </InputAffix>
                                </div>
                                <p className="mb-0 mt-1">{editableTranslate('balance_scorecard.extreme_based_on_overall_result_text_tooltip')} {renderHelpIcon(helpsObj, 'extreme_based_on_overall_result_text_tooltip')}</p>
                              </div>
                            }
                          </div>
                        </div>
                      }

                      <div className="gray-box mb-4 p-1-2">
                        <PerformanceLevelGrid
                          fieldsArray={systematicObjectiveArray}
                          name={'systematicObjective'}
                          showMandatory={watchAll?.systematicMandatoryCommentForExtremeRating === true && watchAll?.systematicMandatoryCommentForExtremeRatingType === 'based_on_single_objective'}
                          control={control} register={register} errors={errors} readOnly={readOnly} helpsObj={helpsObj} />
                      </div>

                      <div className="form-group gray-box">
                        <label className="question-label">{editableTranslate('balance_scorecard.number_of_decimal_places')} {renderHelpIcon(helpsObj, 'systematicNumberOfDecimalPlaces')}</label>
                        <div className="options-div">
                          <DecimalPlaces name="systematicNumberOfDecimalPlaces" control={control} editableTranslate={editableTranslate} readOnly={readOnly} />
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </>
            }

            <div className="section-heading-div" id="fieldset-other_settings">
              <p className="section-title">{editableTranslate('balance_scorecard.other_settings.title')}</p>
              {
                !isCustom && <p className="mt-2">{editableTranslate('balance_scorecard.other_settings.description')} {renderHelpIcon(helpsObj, 'otherSettingsDescription')}</p>
              }
            </div>

            {
              !isCustom && <>
                <div className="pt-3">
                  <p className="font-weight-bold text-default mb-2">{editableTranslate('balance_scorecard.result_presentation.title')}</p>
                  <p>{editableTranslate('balance_scorecard.result_presentation.description')} {renderHelpIcon(helpsObj, 'resultPresentationDescription')}</p>
                </div>

                <div className="row">
                  <div className="col-lg-4">
                    <div className="form-group gray-box">
                      <label className="question-label">{editableTranslate('balance_scorecard.performance_result_calculation')} {renderHelpIcon(helpsObj, 'performanceResultCalculation')}</label>
                      <div className="options-div">
                        <label className="d-flex align-items-center">
                          <input type="radio" value="average" {...register('performanceResultCalculation')} disabled={readOnly} data-testid="performance-result-calculation-average-radio" /> {editableTranslate('balance_scorecard.performance_result_calculation.average')}
                        </label>
                        <label className="d-flex align-items-center">
                          <input type="radio" value="sum_of_all_unweighted" {...register('performanceResultCalculation')} disabled={readOnly} data-testid="performance-result-calculation-sum-of-all-unweighted-radio" /> {editableTranslate('balance_scorecard.performance_result_calculation.sum_of_all_unweighted')}
                        </label>
                        <label className="d-flex align-items-center">
                          <input type="radio" value="sum_of_all_weighted" {...register('performanceResultCalculation')} disabled={readOnly} data-testid="performance-result-calculation-sum-of-all-weighted-radio" /> {editableTranslate('balance_scorecard.performance_result_calculation.sum_of_all_weighted')}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="form-group gray-box">
                      <label className="question-label">{editableTranslate('balance_scorecard.performance_result_rounding')} {renderHelpIcon(helpsObj, 'performanceResultRounding')}</label>
                      <div className="options-div">
                        <label className="d-flex align-items-center">
                          <input type="radio" value="commercially" {...register('performanceResultRounding')} disabled={readOnly} data-testid="performance-result-rounding-commercially-radio" /> {editableTranslate('balance_scorecard.performance_result_rounding.commercially')}
                        </label>
                        <label className="d-flex align-items-center">
                          <input type="radio" value="round_up" {...register('performanceResultRounding')} disabled={readOnly} data-testid="performance-result-rounding-round-up-radio" /> {editableTranslate('balance_scorecard.performance_result_rounding.round_up')}
                        </label>
                        <label className="d-flex align-items-center">
                          <input type="radio" value="round_off" {...register('performanceResultRounding')} disabled={readOnly} data-testid="performance-result-rounding-round-off-radio" /> {editableTranslate('balance_scorecard.performance_result_rounding.round_down')}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="form-group gray-box">
                      <label className="question-label">{editableTranslate('balance_scorecard.number_of_decimal_places')} {renderHelpIcon(helpsObj, 'performanceResultNumberOfDecimalPlaces')}</label>
                      <div className="options-div">
                        <DecimalPlaces name="performanceResultNumberOfDecimalPlaces" control={control} editableTranslate={editableTranslate} readOnly={readOnly} />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="font-weight-bold text-default">{editableTranslate('balance_scorecard.performance_result_map_settings.title')}</p>
                  <p>{editableTranslate('balance_scorecard.performance_result_map_settings.description')} {renderHelpIcon(helpsObj, 'performanceResultMapSettingsDescription')}</p>
                </div>

                <div className="row mb-4">
                  <div className="col-lg-6">
                    <p className="font-weight-bold text-default">{editableTranslate('balance_scorecard.manual_conversion.title')}</p>

                    <div className={`form-group gray-box ${watchAll?.allowChangesOfResult === true ? '' : 'mb-0'}`}>
                      <label className="question-label">{editableTranslate('balance_scorecard.allow_changes_of_result')} {renderHelpIcon(helpsObj, 'allowChangesOfResult')}</label>
                      <div className="options-div">
                        <InlineBoolean control={control} editableTranslate={editableTranslate} name="allowChangesOfResult" readOnly={readOnly} />
                      </div>
                    </div>

                    {
                      watchAll?.allowChangesOfResult === true && <>
                        <div className="form-group gray-box">
                          <label className="question-label">{editableTranslate('balance_scorecard.form_of_performance_map_result')} {renderHelpIcon(helpsObj, 'formOfPerformanceMapResult')}</label>
                          <div className="options-div">
                            <label className="d-inline-flex align-items-center mr-5">
                              <input type="radio" value="fixed_value" {...register('formOfPerformanceMapResult')} disabled={readOnly} data-testid="form-of-performance-map-result-fixed-value-radio" /> {editableTranslate('form.fixed_value')}
                            </label>
                            <label className="d-inline-flex align-items-center">
                              <input type="radio" value="factor" {...register('formOfPerformanceMapResult')} disabled={readOnly} data-testid="form-of-performance-map-result-factor-radio" /> {editableTranslate('form.factor')}
                            </label>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6">
                            <div className="gray-box d-flex flex-wrap align-items-center justify-content-between p-1-2 py-3 mb-1">
                              <label className="peb-field__label text-nowrap mb-0" htmlFor="manual-conversion-lower-limit">{editableTranslate('balance_scorecard.manual_conversion.lower_limit')} {renderHelpIcon(helpsObj, 'manualConversionLowerLimit')}</label>
                              <Input id="manual-conversion-lower-limit" type="text" error={!!errors?.manualConversionLowerLimit} className="input-w-100" {...register('manualConversionLowerLimit')} readOnly={readOnly} data-testid="manual-conversion-lower-limit-input" />
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div className="gray-box d-flex flex-wrap align-items-center justify-content-between p-1-2 py-3 mb-1">
                              <label className="peb-field__label text-nowrap mb-0" htmlFor="manual-conversion-upper-limit">{editableTranslate('balance_scorecard.manual_conversion.upper_limit')} {renderHelpIcon(helpsObj, 'manualConversionUpperLimit')}</label>
                              <Input id="manual-conversion-upper-limit" type="text" error={!!errors?.manualConversionUpperLimit} className="input-w-100" {...register('manualConversionUpperLimit')} readOnly={readOnly} data-testid="manual-conversion-upper-limit-input" />
                            </div>
                          </div>
                        </div>
                      </>
                    }

                    <p className="mb-0">{editableTranslate('balance_scorecard.manual_conversion.important_message')}</p>
                  </div>
                  <div className="col-lg-6">
                    <p className="font-weight-bold text-default">{editableTranslate('balance_scorecard.automatic_conversion.title')}</p>

                    <div className="form-group gray-box">
                      <label className="question-label">{editableTranslate('balance_scorecard.should_convert_result_to_payout_points')} {renderHelpIcon(helpsObj, 'shouldConvertResultToPayoutPoints')}</label>
                      <div className="options-div">
                        <InlineBoolean control={control} editableTranslate={editableTranslate} name="shouldConvertResultToPayoutPoints" readOnly={readOnly} />
                      </div>
                    </div>

                    {
                      watchAll?.shouldConvertResultToPayoutPoints === true && <>
                        <div className="form-group gray-box">
                          <label className="question-label">{editableTranslate('balance_scorecard.result_conversion_type')} {renderHelpIcon(helpsObj, 'resultConversionType')}</label>
                          <div className="options-div">
                            <label className="d-inline-flex align-items-center mr-5">
                              <input type="radio" value="multiple_by_value" {...register('resultConversionType')} disabled={readOnly} data-testid="result-conversion-type-multiple-by-value-radio" /> {editableTranslate('balance_scorecard.result_conversion_type.multiple_by_value')}
                            </label>
                            <label className="d-inline-flex align-items-center">
                              <input type="radio" value="assign_value" {...register('resultConversionType')} disabled={readOnly} data-testid="result-conversion-type-assign-value-radio" /> {editableTranslate('balance_scorecard.result_conversion_type.assign_value')}
                            </label>
                          </div>
                        </div>
                        <div className="gray-box mb-4 p-1-2">
                          <PerformanceMapResultGrid
                            fieldsArray={performanceMapArray}
                            register={register}
                            getValues={getValues}
                            errors={errors}
                            readOnly={readOnly} />
                        </div>
                      </>
                    }
                  </div>
                </div>
              </>
            }

            <div className={isCustom ? 'mt-3' : ''}>
              <p className="font-weight-bold text-default">{editableTranslate('balance_scorecard.comments.title')}</p>
            </div>

            <div className="row">
              <div className="col-lg-6">
                <div className="form-group gray-box">
                  <label className="question-label">{editableTranslate('balance_scorecard.show_balanced_scorecard_notes')} {renderHelpIcon(helpsObj, 'showBalancedScorecardNotes')}</label>
                  <div className="options-div">
                    <InlineBoolean control={control} editableTranslate={editableTranslate} name="showBalancedScorecardNotes" readOnly={readOnly} />
                  </div>
                </div>

                {
                  watchAll?.showBalancedScorecardNotes === true && <div className="form-group gray-box">
                    <label className="question-label">{editableTranslate('balance_scorecard.balanced_scorecard_notes')} {renderHelpIcon(helpsObj, 'balancedScorecardNotes')}</label>
                    <div className="options-div">
                      <EditorInput data-testid="balanced-scorecard-notes-input"
                        value={watchAll['balancedScorecardNotes']}
                        onChangeCallback={(v) => setValue('balancedScorecardNotes', v, { shouldDirty: true, shouldValidate: true })}
                        readOnly={readOnly} />
                    </div>
                  </div>
                }
              </div>
              <div className="col-lg-6">
                <div className="form-group gray-box">
                  <label className="question-label">{editableTranslate('balance_scorecard.show_rating_notes')} {renderHelpIcon(helpsObj, 'showRatingNotes')}</label>
                  <div className="options-div">
                    <InlineBoolean control={control} editableTranslate={editableTranslate} name="showRatingNotes" readOnly={readOnly} />
                  </div>
                </div>

                {
                  watchAll?.showRatingNotes === true && <div className="form-group gray-box">
                    <label className="question-label">{editableTranslate('balance_scorecard.rating_notes')} {renderHelpIcon(helpsObj, 'ratingNotes')}</label>
                    <div className="options-div">
                      <EditorInput data-testid="rating-notes-input"
                        value={watchAll['ratingNotes']}
                        onChangeCallback={(v) => setValue('ratingNotes', v, { shouldDirty: true, shouldValidate: true })}
                        readOnly={readOnly} />
                    </div>
                  </div>
                }
              </div>
            </div>

            {
              !isCustom && <>
                <div>
                  <p className="font-weight-bold text-default">{editableTranslate('balance_scorecard.completed.title')}</p>
                </div>

                <div className="form-group gray-box">
                  <label className="question-label">{editableTranslate('balance_scorecard.completed_behaviour')}</label>
                  <div className="options-div">
                    <label className="d-flex align-items-center">
                      <input type="radio" value="no_changes_possible" {...register('completedBehaviour')} disabled={readOnly} data-testid="completed-behaviour-no-changes-possible-radio" /> {editableTranslate('balance_scorecard.completed_behaviour.no_changes_possible')}
                    </label>
                    <label className="d-flex align-items-center">
                      <input type="radio" value="meetings_can_still_be_updated" {...register('completedBehaviour')} disabled={readOnly} data-testid="completed-behaviour-meetings-can-still-be-updated-radio" /> {editableTranslate('balance_scorecard.completed_behaviour.meetings_can_still_be_updated')}
                    </label>
                  </div>
                </div>

                <div>
                  <p className="font-weight-bold text-default">{editableTranslate('balance_scorecard.report_details.title')}</p>
                </div>

                <div className="form-group gray-box">
                  <label className="question-label">{editableTranslate('balance_scorecard.report_signature')}</label>
                  <div className="options-div">
                    <NativeSelect className="col-lg-6" {...register('reportSignature')} disabled={readOnly} data-testid="report-signature-select">
                      <option value="no_signature">{translate('balance_scorecard.report_signature.no_signature')}</option>
                      <option value="employee_or_supervisor">{translate('balance_scorecard.report_signature.employee_or_supervisor')}</option>
                      <option value="employee_or_supervisor_or_department_head">{translate('balance_scorecard.report_signature.employee_or_supervisor_or_department_head')}</option>
                    </NativeSelect>
                  </div>
                </div>
              </>
            }

            <div className="settings-sticky-footer pb-5">
              <hr className="mt-0" />
              <div className="d-flex justify-content-between">
                <Button data-testid="cancel-button" as={Link} variant="secondary" to="/settings/balance-scorecard">{editableTranslate('button.cancel')}</Button>
                <div>
                  <Button data-testid="preview-button" variant="secondary" onClick={handlePreview} className="mr-3">{editableTranslate('button.preview_balance_score_card')}</Button>
                  <Button type="submit" variant="primary" loading={loading} disabled={readOnly} data-testid="save-button">{editableTranslate('button.save')}</Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {
        tenantIsShadow && <SettingRemarksModal settingRemarks={settingRemarks} handleSaveRemarks={handleSaveRemarks} />
      }
      <PreviewModal show={showPreviewModal} handleClose={handleClosePreviewModal} formData={formData} isPreview={true} />
    </>
  );
}

function SettingCard({ setting }) {
  const { editableTranslate } = useTranslation();

  return setting && <div className="col-12 mb-4 p-0">
    <div className="balancescorecard-setting-card">
      <div className="card-setting-body">
        <div className="row">
          <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.how_to_appraisal')}</div>
          <div className="col-6 card-body-value">{editableTranslate('balance_scorecard.how_to_appraisal.' + setting?.howToAppraisal)}</div>

          {
            setting?.hasDifferentEvaluationLevel === false && <>
              <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.number_of_objective_level')}</div>
              <div className="col-6 card-body-value">{setting?.generalPerformanceLevelGrid?.length}</div>
            </>
          }

          {
            (setting?.howToAppraisal === 'individual_objective_only' || setting?.hasDifferentEvaluationLevel) && <>
              <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.number_of_individual_objective_level')}</div>
              <div className="col-6 card-body-value">{setting?.individualObjectivePerformanceLevelGrid?.length}</div>
            </>
          }

          {
            (setting?.howToAppraisal === 'systematic_objective_only' || setting?.hasDifferentEvaluationLevel) && <>
              <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.number_of_systematic_objective_level')}</div>
              <div className="col-6 card-body-value">{setting?.systematicObjectivePerformanceLevelGrid?.length}</div>
            </>
          }

          <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.performance_result_calculation')}</div>
          <div className="col-6 card-body-value">{editableTranslate('balance_scorecard.performance_result_calculation.' + setting?.performanceResultCalculation)}</div>

          <div className="col-6 card-body-key">{editableTranslate('balance_scorecard.allow_changes_of_result')}</div>
          <div className="col-6 card-body-value">{setting?.allowChangesOfResult ? editableTranslate('form.yes') : editableTranslate('form.no')}</div>
        </div>
      </div>
    </div>
  </div>
}
